import React, { useEffect, useState, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { Check, AlertCircle, Loader, ExternalLink } from 'lucide-react'
import GoogleSetupInstructions from './GoogleSetupInstructions'
import EnvironmentCheck from './EnvironmentCheck'
import toast from 'react-hot-toast'

/**
 * Simple Google Drive Authentication Component
 * Uses Google Identity Services for one-click authentication
 */
const SimpleGoogleDriveAuth = ({ onConnectionSuccess, onConnectionError, className = '' }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userInfo, setUserInfo] = useState(null)
  const { user } = useAuth()
  const googleButtonRef = useRef(null)

  // Google OAuth configuration
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const isClientIdConfigured = GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'your-google-client-id' && GOOGLE_CLIENT_ID.includes('.apps.googleusercontent.com')
  
  const SCOPES = [
    'https://www.googleapis.com/auth/drive',  // Full Google Drive access for file listing/search
    'https://www.googleapis.com/auth/userinfo.profile',
    'https://www.googleapis.com/auth/userinfo.email'
  ].join(' ')

  useEffect(() => {
    // Only load Google services if Client ID is properly configured
    if (!isClientIdConfigured) {
      console.log('Google Client ID not configured, skipping Google services initialization')
      return
    }

    // Load Google Identity Services
    const loadGoogleIdentityServices = () => {
      if (window.google?.accounts) {
        initializeGoogleAuth()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = initializeGoogleAuth
      script.onerror = () => {
        console.error('Failed to load Google Identity Services')
        toast.error('Failed to load Google services')
      }
      document.head.appendChild(script)

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script)
        }
      }
    }

    const initializeGoogleAuth = () => {
      if (!window.google?.accounts) return

      try {
        // Initialize Google Identity Services
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          scope: SCOPES
        })

        // Initialize OAuth for Drive API access
        window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPES,
          callback: handleOAuthResponse
        })

        checkExistingConnection()
      } catch (error) {
        console.error('Error initializing Google Auth:', error)
      }
    }

    loadGoogleIdentityServices()
  }, [isClientIdConfigured])

  /**
   * Check if user already has a Google Drive connection
   */
  const checkExistingConnection = async () => {
    // Skip connection check during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 SimpleGoogleDriveAuth: Onboarding detected, skipping existing connection check')
      return
    }

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        console.log('No auth token found, skipping connection check')
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/status`, {
        headers: {
          'Authorization': `Basic ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.connected) {
          setIsConnected(true)
          setUserInfo(data.userInfo)
        }
      } else if (response.status === 401) {
        console.log('Authentication required for connection check')
      }
    } catch (error) {
      console.log('Connection check failed:', error.message)
    }
  }

  /**
   * Handle Google Identity credential response (for user info)
   */
  const handleCredentialResponse = async (response) => {
    try {
      // This gives us basic user info, but we need OAuth for Drive API access
      console.log('Google credential received')
    } catch (error) {
      console.error('Error handling credential response:', error)
    }
  }

  /**
   * Handle OAuth response (for API access)
   */
  const handleOAuthResponse = async (response) => {
    if (response.error) {
      console.error('OAuth error:', response.error)
      toast.error('Google Drive connection failed')
      onConnectionError?.(response.error)
      setIsLoading(false)
      return
    }

    try {
      // Get user info from Google
      const userResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          'Authorization': `Bearer ${response.access_token}`
        }
      })

      const googleUser = await userResponse.json()

      // Save the connection to our backend
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const saveResponse = await fetch(`${API_BASE_URL}/api/cloud/google-drive/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          expires_in: response.expires_in,
          user_info: googleUser
        })
      })

      if (!saveResponse.ok) {
        throw new Error('Failed to save Google Drive connection')
      }

      const result = await saveResponse.json()
      
      setIsConnected(true)
      setUserInfo(googleUser)
      setIsLoading(false)
      
      toast.success('Google Drive connected successfully!')
      onConnectionSuccess?.(result)

    } catch (error) {
      console.error('Error connecting to Google Drive:', error)
      toast.error('Failed to connect Google Drive')
      onConnectionError?.(error.message)
      setIsLoading(false)
    }
  }

  /**
   * Start Google Drive connection flow
   */
  const handleConnect = async () => {
    if (!isClientIdConfigured) {
      console.log('Google Client ID not configured, showing instructions')
      return
    }

    // Skip connection during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 SimpleGoogleDriveAuth: Onboarding detected, skipping Google Drive connection')
      toast.error('Google Drive connection not available during onboarding')
      return
    }

    if (isLoading) return

    try {
      setIsLoading(true)
      console.log('🔍 SimpleGoogleDriveAuth: Starting connection process...')

      const clientId = GOOGLE_CLIENT_ID
      const redirectUri = `${window.location.origin}/auth/google/callback`
      const scope = SCOPES

      console.log('🔍 SimpleGoogleDriveAuth: OAuth configuration:')
      console.log('- Client ID:', clientId?.substring(0, 20) + '...')
      console.log('- Redirect URI:', redirectUri)
      console.log('- Scope:', scope)

      // Generate OAuth state with user info
      let stateParam = ''
      const authToken = localStorage.getItem('authToken')
      const user = localStorage.getItem('user')
      
      console.log('🔍 SimpleGoogleDriveAuth: Generating OAuth state...')
      console.log('🔍 SimpleGoogleDriveAuth: authToken exists:', !!authToken)
      console.log('🔍 SimpleGoogleDriveAuth: user exists:', !!user)
      console.log('🔍 SimpleGoogleDriveAuth: All localStorage keys:', Object.keys(localStorage))
      
      // Try to get user info from JWT if user key is missing
      let userEmail = null
      if (!user && authToken) {
        console.log('🔍 SimpleGoogleDriveAuth: User key missing, attempting to decode JWT token...')
        try {
          const tokenParts = authToken.split('.')
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]))
            console.log('🔍 SimpleGoogleDriveAuth: Decoded JWT payload:', payload)
            if (payload.email) {
              userEmail = payload.email
              console.log('✅ SimpleGoogleDriveAuth: Found user email in JWT:', userEmail)
            }
          }
        } catch (jwtError) {
          console.warn('⚠️ SimpleGoogleDriveAuth: Failed to decode JWT token:', jwtError)
        }
      }
      
      if (authToken && (user || userEmail)) {
        try {
          let userData
          if (user) {
            userData = JSON.parse(user)
          } else {
            // Create user data from JWT
            userData = { email: userEmail }
          }
          
          console.log('🔍 SimpleGoogleDriveAuth: Using user data:', userData)
          
          const stateData = {
            userEmail: userData.email,
            timestamp: Date.now(),
            sessionId: btoa(authToken.substring(0, 32)).substring(0, 16)
          }
          stateParam = `&state=${encodeURIComponent(JSON.stringify(stateData))}`
          console.log('🔍 SimpleGoogleDriveAuth: Generated state param:', stateParam)
        } catch (error) {
          console.warn('⚠️ SimpleGoogleDriveAuth: Failed to parse user data for OAuth state:', error)
          console.error('⚠️ SimpleGoogleDriveAuth: Raw user data:', user)
        }
      } else {
        console.warn('⚠️ SimpleGoogleDriveAuth: Missing auth data for OAuth state')
        console.warn('⚠️ SimpleGoogleDriveAuth: authToken:', !!authToken, 'user:', !!user, 'userEmail from JWT:', !!userEmail)
      }
      
      // Use standard OAuth flow instead of Google Identity Services
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=code&` +
        `access_type=offline&` +
        `prompt=consent${stateParam}`

      console.log('🔍 SimpleGoogleDriveAuth: Redirecting to OAuth URL:', authUrl.replace(/client_id=[^&]*/, 'client_id=***'))

      // Redirect to Google OAuth
      window.location.href = authUrl

    } catch (error) {
      console.error('❌ SimpleGoogleDriveAuth: Error starting OAuth:', error)
      setIsLoading(false)
      if (onConnectionError) {
        onConnectionError(error.message)
      }
    }
  }

  const handleDisconnect = async () => {
    // Skip disconnection during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 SimpleGoogleDriveAuth: Onboarding detected, skipping Google Drive disconnection')
      toast.error('Google Drive disconnection not available during onboarding')
      return
    }

    try {
      setIsLoading(true)
      
      const authToken = localStorage.getItem('authToken')
      if (!authToken) {
        toast.error('No authentication token found. Please log in again.')
        return
      }
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${authToken}`
        }
      })

      if (response.ok) {
        setIsConnected(false)
        setUserInfo(null)
        console.log('✅ SimpleGoogleDriveAuth: Successfully disconnected from Google Drive')
        
        if (onConnectionSuccess) {
          onConnectionSuccess({ disconnected: true })
        }
      } else {
        throw new Error('Failed to disconnect')
      }
    } catch (error) {
      console.error('❌ SimpleGoogleDriveAuth: Error disconnecting:', error)
      if (onConnectionError) {
        onConnectionError(error.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleDebugToken = async () => {
    // Skip debug token check during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 SimpleGoogleDriveAuth: Onboarding detected, skipping debug token check')
      toast.error('Debug token check not available during onboarding')
      return
    }

    try {
      console.log('🔍 SimpleGoogleDriveAuth: Checking token debug info...')
      
      const authToken = localStorage.getItem('authToken')
      if (!authToken) {
        toast.error('No authentication token found. Please log in again.')
        return
      }
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/debug-token`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      })

      if (response.ok) {
        const debugInfo = await response.json()
        console.log('🔍 DEBUG TOKEN INFO:', debugInfo)
        
        // Show debug info in a more readable format
        if (debugInfo.tokenValid && debugInfo.scope) {
          const scopes = debugInfo.scope.split(' ')
          const hasDriveScope = scopes.includes('https://www.googleapis.com/auth/drive')
          const hasDriveFileScope = scopes.includes('https://www.googleapis.com/auth/drive.file')
          
          console.log('🔍 TOKEN SCOPES ANALYSIS:')
          console.log('- Has full drive scope:', hasDriveScope)
          console.log('- Has drive.file scope:', hasDriveFileScope)
          console.log('- All scopes:', scopes)
          
          toast.success(`Token Debug: ${hasDriveScope ? '✅ Has full drive scope' : '❌ Missing full drive scope'}`)
        } else if (debugInfo.hasProvider === false) {
          toast.error('❌ No Google Drive provider found. Please connect Google Drive first.')
        } else if (debugInfo.hasToken === false) {
          toast.error('❌ Google Drive connected but no access token stored.')
        } else if (debugInfo.tokenValid === false) {
          toast.error('❌ Google Drive token is invalid or expired.')
        } else {
          toast.error('❌ Token is invalid or expired')
        }
      } else {
        const errorText = await response.text()
        console.error('Debug response error:', response.status, errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }
    } catch (error) {
      console.error('❌ SimpleGoogleDriveAuth: Error getting debug info:', error)
      toast.error('Debug check failed: ' + error.message)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
          <Loader className="w-5 h-5 animate-spin" />
          <span>Connecting to Google Drive...</span>
        </div>
      </div>
    )
  }

  // Connected state
  if (isConnected && userInfo) {
    return (
      <div className={`bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 ${className}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex-shrink-0">
              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium text-green-900 dark:text-green-100">Google Drive Connected</h3>
              <p className="text-sm text-green-700 dark:text-green-300 truncate">
                Connected as {userInfo.name} ({userInfo.email})
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              onClick={handleDebugToken}
              className="w-full sm:w-auto px-3 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
              title="Debug token scopes"
            >
              Debug
            </button>
            <button
              onClick={handleDisconnect}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Disconnect
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Not connected state
  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24">
              <path fill="currentColor" d="M6.28 3l5.24 9.07L15.76 3z"/>
              <path fill="currentColor" d="M23.44 12l-5.24-9.07H23.44z"/>
              <path fill="currentColor" d="M.56 12l5.24 9.07H.56z"/>
              <path fill="currentColor" d="M12 13.93L6.76 21H23.44z"/>
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Google Drive</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sync your notes to Google Drive
            </p>
          </div>
        </div>
        <button
          onClick={handleConnect}
          disabled={!isClientIdConfigured}
          className={`w-full sm:w-auto px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-2 ${
            isClientIdConfigured 
              ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600' 
              : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
          }`}
        >
          <ExternalLink className="w-4 h-4" />
          <span>{isClientIdConfigured ? 'Connect' : 'Setup Required'}</span>
        </button>
      </div>

      {/* Setup Instructions */}
      <div className="mt-4 space-y-4">
        <GoogleSetupInstructions />
        <EnvironmentCheck />
      </div>
    </div>
  )
}

export default SimpleGoogleDriveAuth 